import { useEffect, useState } from 'react'
import { FeeLocation } from '../../../enums/FeeLocation'
import { FeePosition } from '../../../enums/FeePosition'
import { FeeType } from '../../../enums/FeeType'
import { InspectionFeeFamily } from '../../../enums/InspectionFeeFamily'
import { SiteOption } from '../../../types/SiteOption'
import { InspectionVariableFeeTemplate, VariableFeeRow } from '../../../types/VariableFee'
import { useSites } from '../../../hooks/useSites'
import { getDefaultFeeRows } from '../constants'
import { applyDataToRows, getGlobalRow } from '../utils/variableFeeUtils'
import { buildSaveTargetQuery, SaveTarget } from '../moduleVoNavigation'
import BackOfficeAPIClient from '../../../clients/BackofficeAPIClient'

const backofficeApiClient = new BackOfficeAPIClient()

interface UseVariableFeesReturn
{
    sites: SiteOption[]
    rows: VariableFeeRow[]
    setRows: (rows: VariableFeeRow[]) => void
    globalData: InspectionVariableFeeTemplate[]
    selectedSite: SiteOption | null
    setSelectedSite: (site: SiteOption | null) => void
    hasChanges: boolean
    submitting: boolean
    loading: boolean
    saveError: string | null
    getGlobalRow: (row: VariableFeeRow) => InspectionVariableFeeTemplate | undefined
    updateAmount: (family: InspectionFeeFamily, location: FeeLocation, position: FeePosition | null, feeType: FeeType, value: string) => void
    toggleActive: (family: InspectionFeeFamily, location: FeeLocation, position: FeePosition | null, feeType: FeeType, checked: boolean) => void
    save: (target: SaveTarget) => Promise<void>
}

/**
 * Ne fetch réellement que le global et, si un site est sélectionné, ce site (fusionné avec le
 * global comme repli). Toutes les autres portées (marque seule, plusieurs sites/marques, ou une
 * combinaison site+marque) sont calculées côté composant (VariableFees.tsx) à partir de rawData
 * (useVariableFeeRawData) — ce hook ne les connaît pas.
 */
export function useVariableFees(): UseVariableFeesReturn {
    const sites = useSites()
    const [ rows, setRows ] = useState<VariableFeeRow[]>(getDefaultFeeRows())
    const [ globalData, setGlobalData ] = useState<InspectionVariableFeeTemplate[]>([])
    const [ selectedSite, setSelectedSite ] = useState<SiteOption | null>(null)
    const [ hasChanges, setHasChanges ] = useState(false)
    const [ submitting, setSubmitting ] = useState(false)
    const [ saveError, setSaveError ] = useState<string | null>(null)
    const [ loadingGlobal, setLoadingGlobal ] = useState(true)
    const [ loadingSite, setLoadingSite ] = useState(false)

    const selectedSiteId = selectedSite?.value ?? null

    const fetchGlobalConfig = async () => {
        setLoadingGlobal(true)
        try {
            const data = await backofficeApiClient.get<InspectionVariableFeeTemplate[]>('/inspection-variable-fee-templates')
            setGlobalData(data)
            setRows(applyDataToRows(getDefaultFeeRows(), data))
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingGlobal(false)
        }
    }

    const fetchSiteConfig = async (siteId: number) => {
        setLoadingSite(true)
        try {
            const data = await backofficeApiClient.get<InspectionVariableFeeTemplate[]>(`/inspection-variable-fee-templates?dealership_id=${siteId}`)
            setRows(applyDataToRows(applyDataToRows(getDefaultFeeRows(), globalData), data))
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingSite(false)
        }
    }

    useEffect(() => {
        fetchGlobalConfig()
    }, [])

    useEffect(() => {
        if (selectedSiteId) {
            fetchSiteConfig(selectedSiteId)
        } else {
            setRows(applyDataToRows(getDefaultFeeRows(), globalData))
            setLoadingSite(false)
        }
        setHasChanges(false)
    }, [ selectedSiteId, globalData ])

    // Le premier render affiche les valeurs globales (fetchGlobalConfig), puis, si un site est
    // sélectionné, fetchSiteConfig les remplace : sans ce flag, l'UI affiche brièvement les
    // valeurs globales avant de basculer sur celles du site, un flash visible pour l'utilisateur.
    const loading = loadingGlobal || (selectedSiteId != null && loadingSite)

    const updateAmount = (
        family: InspectionFeeFamily,
        location: FeeLocation,
        position: FeePosition | null,
        feeType: FeeType,
        value: string,
    ) => {
        setRows(prev => prev.map(row =>
            row.family === family && row.location === location && row.position === position && row.feeType === feeType
                ? { ...row, averageAmount: value === '' ? null : Number(value) }
                : row,
        ))
        setHasChanges(true)
    }

    const toggleActive = (
        family: InspectionFeeFamily,
        location: FeeLocation,
        position: FeePosition | null,
        feeType: FeeType,
        checked: boolean,
    ) => {
        setRows(prev => prev.map(row =>
            row.family === family && row.location === location && row.position === position && row.feeType === feeType
                ? { ...row, isActive: checked }
                : row,
        ))
        setHasChanges(true)
    }

    // N'écrase `rows` directement qu'après un save global ou site-seul (dont ce hook connaît le
    // fetch). Pour les autres cibles (marque, multi-sites/marques, combo), c'est le composant qui
    // rafraîchit rawData et relance le calcul — cf. VariableFees.tsx : save(...).then(refetchRawData).
    const save = async (target: SaveTarget) => {
        setSubmitting(true)
        setSaveError(null)
        try {
            const payload = rows
                .filter(row => row.averageAmount !== null)
                .map(row => ({
                    family: row.family,
                    location: row.location,
                    position: row.position,
                    fee_type: row.feeType,
                    is_active: row.isActive,
                    amount: row.averageAmount !== null ? Math.round(row.averageAmount * 100) : null,
                }))

            const query = buildSaveTargetQuery(target)

            await backofficeApiClient.put(`/inspection-variable-fee-templates${query}`, payload)

            if (target.dealershipIds.length === 1 && target.brands.length === 0) {
                await fetchSiteConfig(target.dealershipIds[0])
            } else if (target.dealershipIds.length === 0 && target.brands.length === 0) {
                await fetchGlobalConfig()
            }

            setHasChanges(false)
        } catch (error: any) {
            console.log(error)
            setSaveError(error?.message ?? 'Une erreur est survenue lors de la sauvegarde')
        } finally {
            setSubmitting(false)
        }
    }

    return {
        sites,
        rows,
        setRows,
        globalData,
        selectedSite,
        setSelectedSite,
        hasChanges,
        submitting,
        loading,
        saveError,
        getGlobalRow: (row) => getGlobalRow(globalData, row),
        updateAmount,
        toggleActive,
        save,
    }
}
